/* eslint-disable max-lines-per-function */
import React, { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Button } from "components/haven-design/buttons";
import { getQueryParam } from "utils/url";
import { useSidebarFilters } from "pages/dashboard/interests/sidebar-filters";
import { useSearchManager } from "pages/dashboard/interests/interests-table";
import styles from "./navigation.module.css";

type Props = {
  interestID: string;
  listID: string;
  defaultSorting: string;
}


const Navigation: React.FC<Props & {tabName: string }> = ({ tabName, listID, defaultSorting }) => {
  const rawOffset = getQueryParam("offset");
  const initialOffset = rawOffset ? Number(rawOffset) : -1;
  const [offset, setOffset] = React.useState(initialOffset);
  const [navigateToId, setNavigateToId] = React.useState<null | number>(null);
  const navigate = useNavigate();
  const {
    searchState: { page: resultsPage, hits, nbHits: total },
    pageState: { pageSize },
    interestListIds,
    setInterestListIds,
  } = useSidebarFilters();

  useEffect(() => {
    if (interestListIds.length === 0) {
      setInterestListIds([listID]);
    }
  }, [interestListIds]);

  const { setSelectedPage, loading } = useSearchManager(defaultSorting);

  const doNavigate = (interestID:string, nextOffset:number) => {
    setOffset(nextOffset);
    const url = `/dash/manager/interest/${interestID}/${tabName}?offset=${nextOffset}`;

    navigate(url);
  };

  const onNavigate = (direction: "next" | "prev") => {
    // if offset is within page * pageSize and (page + 1)*pageSize - 1
    // then fetch id and display page
    // otherwise, increment or decrement page, refetch, then get ID

    const target = direction === "next" ? offset + 1 : Math.max(0, offset - 1);
    setNavigateToId(target);
  };

  useEffect(() => {
    if (navigateToId && !loading) {
      const targetResult = hits[navigateToId - (resultsPage * pageSize)];
      if (targetResult) {
        setNavigateToId(null);
        doNavigate(targetResult.id, navigateToId);
      } else {
        const newPage = Math.ceil((navigateToId + 1) / pageSize) - 1;
        setSelectedPage(newPage);
      }
    }
  }, [navigateToId, hits, loading]);

  if (!hits.length) return null;
  if (offset === -1) return null;


  return (
    <div className={styles.container}>
      <Button
        size="S"
        color="secondary"
        onClick={() => onNavigate("prev")}
        disabled={offset === 0}
      ><ChevronLeftIcon fontSize="small" /></Button>
      <div className={styles.offsets}>
        {(offset || 0) + 1} of {total}
      </div>
      <Button
        size="S"
        color="secondary"
        onClick={() => onNavigate("next")}
        disabled={offset + 1 === total}
      ><ChevronRightIcon fontSize="small" /></Button>
    </div>
  );
};

const NavigateWrapper: React.FC<Props> = (props) => {
  const { tabName } = useParams<{ tabName?: string }>();
  const computedTab = tabName ?? "activity";
  return <Navigation {...props} tabName={computedTab} />;
};

export default NavigateWrapper;
